/**
 * The registry export dialog - phase 11.
 *
 * Export is the reason this application exists, so it gets a real dialog
 * rather than a bare link: the row count stated up front, a choice of file
 * format, and column selection with every column on by default. The download
 * itself stays a plain same-origin `<a href>` to the streamed endpoint, the
 * same reason `companiesApi.exportUrl` is a URL and not a fetch - the browser
 * writes the response straight to disk, and a 200,000-row file never has to
 * fit in memory here first.
 */

import { useEffect, useMemo, useState } from 'react';
import { companiesApi } from '../lib/api.js';
import { IconAlert, IconDownload } from './Icons.jsx';

/**
 * Grouping is presentational only - the export itself is a flat column list,
 * this just keeps forty checkboxes from reading as one undifferentiated wall.
 */
const COLUMN_GROUPS = [
  { heading: 'Identity', keys: ['company_number', 'name', 'status', 'type', 'incorporated_on', 'trading_age_years'] },
  { heading: 'Classification & financials', keys: ['sic_codes', 'agency_verdict', 'size_band', 'trading_verdict', 'latest_net_assets', 'latest_net_assets_period_end', 'latest_employee_count', 'latest_turnover'] },
  { heading: 'Address', keys: ['address_line_1', 'address_line_2', 'locality', 'region', 'postal_code', 'country'] },
  { heading: 'Contact', keys: ['website', 'primary_director', 'chosen_email', 'chosen_email_verify_status'] },
];

function formatNumber(value) {
  return typeof value === 'number' ? value.toLocaleString() : '—';
}

export default function ExportDialog({ filters, total, onClose }) {
  const [columns, setColumns] = useState(null);
  const [error, setError] = useState(null);
  const [format, setFormat] = useState('csv');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    companiesApi
      .exportColumns()
      .then((payload) => {
        if (cancelled) return;
        setColumns(payload.columns);
        // Every column on by default - the brief's "sensible default" is
        // everything, not a curated subset an operator has to expand.
        setSelected(new Set(payload.columns.map((c) => c.key)));
      })
      .catch((caught) => {
        if (!cancelled) setError(caught.message ?? 'Could not load the column list.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const toggleColumn = (key) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set((columns ?? []).map((c) => c.key)));
  const selectNone = () => setSelected(new Set());

  const columnCount = selected?.size ?? 0;
  const canDownload = columnCount > 0 && total > 0;

  const downloadUrl = useMemo(() => {
    if (!columns) return null;
    return companiesApi.exportUrl({
      ...filters,
      format,
      // Sent in the catalogue's own order, not selection order, so the file's
      // column order does not depend on the sequence boxes were ticked in.
      columns: columns
        .map((c) => c.key)
        .filter((key) => selected?.has(key))
        .join(','),
    });
  }, [columns, filters, format, selected]);

  return (
    <div className="scrim" onClick={onClose} role="presentation">
      <div
        className="dialog dialog-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="dialog-header">
          <div>
            <h2 className="dialog-title" id="export-title">
              Export companies
            </h2>
            <p className="dialog-description">
              {total > 0
                ? `${formatNumber(total)} compan${total === 1 ? 'y matches' : 'ies match'} the filters currently applied to the registry.`
                : 'No company matches the filters currently applied - clear or change a filter before exporting.'}
            </p>
          </div>
          <button type="button" className="btn btn-ghost btn-sm dialog-close" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="dialog-body export-grid">
          <section className="export-col">
            <h3 className="acquire-heading">File format</h3>
            <div className="pill-row">
              <button
                type="button"
                className={`pill${format === 'csv' ? ' is-active' : ''}`}
                onClick={() => setFormat('csv')}
              >
                CSV
              </button>
              <button
                type="button"
                className={`pill${format === 'xlsx' ? ' is-active' : ''}`}
                onClick={() => setFormat('xlsx')}
              >
                Excel (.xlsx)
              </button>
            </div>
            <p className="text-xs text-dim">
              {format === 'csv'
                ? 'A plain-text file every spreadsheet and CRM can import.'
                : 'A real Excel workbook - numbers and dates stay typed as numbers and dates rather than text.'}
            </p>

            <p className="text-xs text-faint">
              Streamed straight to disk as it is generated, so a large export does not need to fit in
              memory here first.
            </p>
          </section>

          <section className="export-col">
            <div className="row row-between">
              <h3 className="acquire-heading">
                Columns
                <span className="pill-count">{columnCount}</span>
              </h3>
              <span className="row row-tight">
                <button type="button" className="btn btn-ghost btn-sm" onClick={selectAll}>
                  All
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={selectNone}>
                  None
                </button>
              </span>
            </div>

            {error ? (
              <div className="alert alert-error">
                <span className="alert-icon">
                  <IconAlert />
                </span>
                <span className="alert-body">{error}</span>
              </div>
            ) : !columns ? (
              <p className="text-sm text-dim">Loading the column list…</p>
            ) : (
              <div className="export-columns">
                {COLUMN_GROUPS.map((group) => (
                  <div key={group.heading} className="export-column-group">
                    <span className="export-column-group-heading">{group.heading}</span>
                    {group.keys.map((key) => {
                      const column = columns.find((c) => c.key === key);
                      if (!column) return null;
                      return (
                        <label key={key} className="checkbox-row">
                          <input
                            type="checkbox"
                            className="checkbox"
                            checked={selected?.has(key) ?? false}
                            onChange={() => toggleColumn(key)}
                          />
                          {column.label}
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="dialog-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <span className="dialog-footer-spacer" />
          <a
            className={`btn btn-primary${canDownload && downloadUrl ? '' : ' is-disabled'}`}
            href={canDownload && downloadUrl ? downloadUrl : undefined}
            aria-disabled={!canDownload || !downloadUrl}
            onClick={(event) => {
              if (!canDownload || !downloadUrl) {
                event.preventDefault();
                return;
              }
              onClose();
            }}
          >
            <IconDownload />
            Download {total > 0 ? formatNumber(total) : ''}
          </a>
        </div>
      </div>
    </div>
  );
}
